import { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { HardDrive, Image as ImageIcon, Music, Trash2, Search, RefreshCw, ShieldAlert, Scan, Play, Pause, Eye, AlertTriangle, CheckCircle2, FileWarning, ListChecks, Server } from "lucide-react";

const DELETE_REASONS = [
  "Duplicate File",
  "Old Artwork",
  "Old Audio",
  "Unused Upload",
  "Test File",
  "Other",
];

const STATUS_BADGE = {
  in_use: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  orphaned: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  missing_db: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};
const STATUS_LABEL = {
  in_use: "In Use – Protected",
  orphaned: "Unused / Orphaned",
  missing_db: "Missing File",
};

function StatCard({ icon: Icon, label, value, sub, tint = "cyan", testid }) {
  const tints = {
    cyan: "from-cyan-500/10 to-transparent border-cyan-500/20 text-cyan-300",
    emerald: "from-emerald-500/10 to-transparent border-emerald-500/20 text-emerald-300",
    amber: "from-amber-500/10 to-transparent border-amber-500/20 text-amber-300",
    violet: "from-violet-500/10 to-transparent border-violet-500/20 text-violet-300",
    rose: "from-rose-500/10 to-transparent border-rose-500/20 text-rose-300",
    sky: "from-sky-500/10 to-transparent border-sky-500/20 text-sky-300",
  };
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${tints[tint]} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="text-2xl font-bold mt-2 text-zinc-100">{value}</div>
      {sub && <div className="text-xs text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

function InlineAudio({ url, testid }) {
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);
  const [aud, setAud] = useState(null);

  const toggle = () => {
    if (!aud) return;
    if (playing) { aud.pause(); } else { aud.play(); }
  };

  return (
    <div className="flex items-center gap-2 min-w-[220px]" data-testid={testid}>
      <Button size="sm" variant="outline" onClick={toggle}
        className="h-7 w-7 p-0 rounded-full border-zinc-700"
        data-testid={`${testid}-toggle`}>
        {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
      </Button>
      <div className="text-xs text-zinc-500 tabular-nums min-w-[80px]">
        {fmtTime(cur)} / {fmtTime(dur)}
      </div>
      <div className="relative h-1.5 flex-1 bg-zinc-800 rounded overflow-hidden">
        <div className="absolute top-0 left-0 bottom-0 bg-gradient-to-r from-cyan-400 to-violet-400"
          style={{ width: dur ? `${(cur / dur) * 100}%` : "0%" }} />
      </div>
      <audio ref={setAud}
        preload="none"
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        src={fileUrl(url)}
      />
    </div>
  );
}

function fmtTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminStorageManager() {
  const [tab, setTab] = useState("artwork");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selection, setSelection] = useState(new Set());
  const [scan, setScan] = useState(null);
  const [preview, setPreview] = useState(null); // artwork preview {url, filename}
  const [confirm, setConfirm] = useState(null); // {row, force}
  const [bulkConfirm, setBulkConfirm] = useState(null); // {items, freeBytes}
  const [reason, setReason] = useState("Unused Upload");
  const [history, setHistory] = useState([]);

  const loadSummary = async () => {
    try {
      const r = await api.get("/admin/storage/summary");
      setSummary(r.data);
    } catch (e) {
      toast.error("Failed to load storage summary");
    }
  };

  const loadFiles = async (kind = tab, s = search, st = statusFilter) => {
    if (kind === "history") { await loadHistory(); return; }
    setLoading(true);
    try {
      const r = await api.get("/admin/storage/files", {
        params: { kind, search: s, status: st, limit: 1000 }
      });
      setRows(r.data.items || []);
      setSelection(new Set());
    } catch (e) {
      toast.error("Failed to load files");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/storage/deletion-history", { params: { limit: 300 } });
      setHistory(r.data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadFiles(tab, search, statusFilter); /* eslint-disable-next-line */ }, [tab, statusFilter]);

  const runScan = async () => {
    try {
      const r = await api.post("/admin/storage/scan");
      setScan(r.data);
      toast.success(`Scan complete — ${r.data.freeable_human} freeable`);
      loadSummary();
    } catch (e) {
      toast.error("Scan failed");
    }
  };

  const toggleSelect = (fname) => {
    setSelection(prev => {
      const s = new Set(prev);
      if (s.has(fname)) s.delete(fname); else s.add(fname);
      return s;
    });
  };

  const selectableRows = useMemo(() => rows.filter(r => r.usage_status === "orphaned"), [rows]);

  const toggleAllOrphans = () => {
    if (selection.size === selectableRows.length && selectableRows.length > 0) {
      setSelection(new Set());
    } else {
      setSelection(new Set(selectableRows.map(r => r.filename)));
    }
  };

  const performDelete = async () => {
    if (!confirm) return;
    const { row, force } = confirm;
    try {
      const res = await api.delete("/admin/storage/file", {
        data: { kind: row.kind, filename: row.filename, force, reason }
      });
      toast.success(`Deleted ${row.filename} — freed ${res.data.size_human}${res.data.was_forced ? " (forced)" : ""}`);
      setConfirm(null);
      await loadSummary();
      await loadFiles();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Delete failed");
      toast.error(msg);
    }
  };

  const openBulkConfirm = () => {
    const items = rows.filter(r => selection.has(r.filename) && r.usage_status === "orphaned");
    const freeBytes = items.reduce((a, b) => a + (b.size || 0), 0);
    if (items.length === 0) {
      toast.info("Select at least one orphaned file");
      return;
    }
    setBulkConfirm({ items, freeBytes });
  };

  const performBulk = async () => {
    if (!bulkConfirm) return;
    try {
      const payload = { items: bulkConfirm.items.map(r => ({ kind: r.kind, filename: r.filename })), reason };
      const res = await api.post("/admin/storage/bulk-delete", payload);
      toast.success(`Deleted ${res.data.deleted_count} files · Freed ${res.data.freed_human}`);
      if (res.data.error_count > 0) toast.error(`${res.data.error_count} skipped (in use or invalid)`);
      setBulkConfirm(null);
      await loadSummary();
      await loadFiles();
    } catch (e) {
      toast.error("Bulk delete failed");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 space-y-6" data-testid="admin-storage-manager">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">Sonotek Digi · Administrator</div>
          <h1 className="text-3xl font-bold mt-1">Storage Manager</h1>
          <p className="text-zinc-400 text-sm">Inspect Artwork & Audio storage, find unused files, safely reclaim disk space.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { loadSummary(); loadFiles(); }} data-testid="storage-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={runScan} className="bg-gradient-to-r from-cyan-500 to-violet-500 text-zinc-950 font-semibold"
            data-testid="storage-scan-btn">
            <Scan className="w-4 h-4 mr-1" /> Scan for Unused Files
          </Button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="storage-summary">
          <StatCard icon={HardDrive} label="Total Storage Used" value={summary.total_human}
            sub={`${summary.total_files} files`} tint="cyan" testid="stat-total" />
          <StatCard icon={ImageIcon} label="Artwork Storage" value={summary.artwork.total_human}
            sub={`${summary.artwork.total_files} files`} tint="sky" testid="stat-artwork" />
          <StatCard icon={Music} label="Audio Storage" value={summary.audio.total_human}
            sub={`${summary.audio.total_files} files`} tint="violet" testid="stat-audio" />
          <StatCard icon={CheckCircle2} label="Artwork In Use" value={summary.artwork.in_use}
            sub={`${summary.artwork.orphaned} unused`} tint="emerald" testid="stat-art-in-use" />
          <StatCard icon={CheckCircle2} label="Audio In Use" value={summary.audio.in_use}
            sub={`${summary.audio.orphaned} unused`} tint="emerald" testid="stat-aud-in-use" />
          <StatCard icon={FileWarning} label="Orphaned Files" value={summary.total_orphaned}
            sub="No DB reference" tint="amber" testid="stat-orphaned" />
          <StatCard icon={Trash2} label="Can Free" value={summary.freeable_human}
            sub="Safe to delete" tint="rose" testid="stat-freeable" />
        </div>
      )}

      {/* Storage Location */}
      {summary && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 text-xs text-zinc-400 flex items-center gap-3"
          data-testid="storage-location-info">
          <Server className="w-4 h-4 text-cyan-400" />
          <span><span className="text-zinc-500">Provider:</span> <span className="text-zinc-200">{summary.storage_provider}</span></span>
          <span className="text-zinc-700">·</span>
          <span><span className="text-zinc-500">Root:</span> <span className="text-zinc-200 font-mono">{summary.storage_root}</span></span>
          <span className="text-zinc-700">·</span>
          <span className="text-emerald-400/80">No API keys / secrets exposed</span>
        </div>
      )}

      {/* Scan results banner */}
      {scan && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="scan-results">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-400 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-300">Scan complete</div>
              <div className="text-sm text-zinc-300 mt-1">
                Found <span className="text-amber-300 font-semibold">{scan.artwork_orphaned_count}</span> orphaned artwork
                (<span className="text-amber-300">{scan.artwork_orphaned_human}</span>),
                <span className="text-amber-300 font-semibold"> {scan.audio_orphaned_count}</span> orphaned audio
                (<span className="text-amber-300">{scan.audio_orphaned_human}</span>).
                Missing files referenced by DB — Artwork: <span className="text-rose-300">{scan.artwork_missing_db_files.length}</span>,
                Audio: <span className="text-rose-300">{scan.audio_missing_db_files.length}</span>.
                Nothing deleted — review below.
              </div>
              <div className="text-lg font-bold text-amber-300 mt-2">
                Freeable: {scan.freeable_human}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setScan(null)}>Dismiss</Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "history") loadHistory(); }}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="artwork" data-testid="tab-artwork"><ImageIcon className="w-4 h-4 mr-1" /> Artwork</TabsTrigger>
          <TabsTrigger value="audio" data-testid="tab-audio"><Music className="w-4 h-4 mr-1" /> Audio</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><ListChecks className="w-4 h-4 mr-1" /> Deletion History</TabsTrigger>
        </TabsList>

        {tab !== "history" && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadFiles(); }}
                placeholder="Search filename, song, user, ISRC, UPC, submission ID…"
                className="pl-9 bg-zinc-900 border-zinc-800" data-testid="storage-search-input" />
            </div>
            <Button variant="outline" onClick={() => loadFiles()} data-testid="storage-search-btn">Search</Button>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800" data-testid="storage-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="orphaned">Unused / Orphaned</SelectItem>
                <SelectItem value="in_use">In Use</SelectItem>
                <SelectItem value="missing_db">Missing File (DB ref)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800" data-testid="storage-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DELETE_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            {selection.size > 0 && (
              <Button onClick={openBulkConfirm} className="bg-rose-500 hover:bg-rose-600 text-white"
                data-testid="storage-bulk-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete Selected Unused ({selection.size})
              </Button>
            )}
          </div>
        )}

        <TabsContent value="artwork" className="mt-3">
          <FileTable
            rows={rows}
            loading={loading}
            kind="artwork"
            selection={selection}
            toggleSelect={toggleSelect}
            toggleAllOrphans={toggleAllOrphans}
            selectableRows={selectableRows}
            setPreview={setPreview}
            setConfirm={setConfirm}
          />
        </TabsContent>

        <TabsContent value="audio" className="mt-3">
          <FileTable
            rows={rows}
            loading={loading}
            kind="audio"
            selection={selection}
            toggleSelect={toggleSelect}
            toggleAllOrphans={toggleAllOrphans}
            selectableRows={selectableRows}
            setPreview={setPreview}
            setConfirm={setConfirm}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-3">
          <Card className="bg-zinc-950/60 border-zinc-800">
            <CardHeader><CardTitle className="text-base">Deletion Audit Log</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead>When</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Song</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Was In Use?</TableHead>
                    <TableHead>Deleted By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-6">No deletions yet.</TableCell></TableRow>
                  )}
                  {history.map((h) => (
                    <TableRow key={h.id} className="border-zinc-800" data-testid={`history-row-${h.id}`}>
                      <TableCell className="text-xs text-zinc-400">{fmtDate(h.deleted_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{h.filename}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{h.kind}</Badge></TableCell>
                      <TableCell className="text-xs">{h.size_human}</TableCell>
                      <TableCell className="text-xs text-zinc-300">{h.song_title || "—"}</TableCell>
                      <TableCell className="text-xs text-zinc-300">{h.reason}</TableCell>
                      <TableCell>
                        {h.was_in_use ? (
                          <Badge className="bg-rose-500/10 text-rose-300 border-rose-500/30 border">
                            {h.forced ? "Yes · Forced" : "Yes"}
                          </Badge>
                        ) : <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 border">No</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-zinc-400">{h.deleted_by_name || h.deleted_by_email || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Artwork Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-sm font-mono text-zinc-200">{preview?.filename}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="flex justify-center">
              <img src={fileUrl(preview.url)} alt={preview.filename}
                className="max-w-full max-h-[70vh] rounded-lg border border-zinc-800" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-400">
              <AlertTriangle className="w-5 h-5" />
              {confirm?.force ? "Force Delete In-Use File?" : "Delete this file permanently?"}
            </DialogTitle>
            <DialogDescription>
              This action <span className="text-rose-300 font-semibold">cannot be undone</span> — the physical file
              will be removed from disk. Song metadata, ISRC/UPC, revenue, DSP links and other records will be preserved.
            </DialogDescription>
          </DialogHeader>
          {confirm && (
            <div className="space-y-2 text-sm bg-zinc-950 border border-zinc-800 rounded-lg p-3">
              <Row label="File Name" val={<span className="font-mono">{confirm.row.filename}</span>} />
              <Row label="Kind" val={confirm.row.kind} />
              <Row label="Size" val={confirm.row.size_human} />
              <Row label="Usage" val={<StatusBadge status={confirm.row.usage_status} />} />
              {confirm.row.usage?.[0] && <>
                <Row label="Song" val={confirm.row.usage[0].song_title || "—"} />
                <Row label="Owner" val={confirm.row.usage[0].user_name || confirm.row.usage[0].user_email || "—"} />
                <Row label="ISRC / UPC" val={`${confirm.row.usage[0].isrc || "—"} / ${confirm.row.usage[0].upc || "—"}`} />
              </>}
              {confirm.force && (
                <div className="mt-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                  ⚠ This file is currently attached to the song above. Deleting it will remove the file and clear
                  the {confirm.row.kind === "artwork" ? "artwork_url" : "audio_url"} on the song. The song record
                  itself (ISRC/UPC/revenue/DSP links) will remain intact.
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="delete-cancel-btn">Cancel</Button>
            <Button className="bg-rose-500 hover:bg-rose-600 text-white" onClick={performDelete}
              data-testid="delete-confirm-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Confirm Permanent Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Confirm */}
      <Dialog open={!!bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-400">
              <AlertTriangle className="w-5 h-5" /> Delete Selected Unused Files
            </DialogTitle>
            <DialogDescription>
              {bulkConfirm && <>
                <span className="text-rose-300 font-semibold">{bulkConfirm.items.length} files</span> will be permanently
                deleted from disk — freeing <span className="text-rose-300 font-semibold">{humanBytes(bulkConfirm.freeBytes)}</span>.
                Only orphaned files are removed. In-use files are protected and will be skipped automatically.
              </>}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[240px] overflow-y-auto text-xs font-mono bg-zinc-950 border border-zinc-800 rounded p-2 space-y-1">
            {bulkConfirm?.items.map(i => (
              <div key={i.filename} className="flex justify-between">
                <span className="text-zinc-300 truncate">{i.filename}</span>
                <span className="text-zinc-500 ml-2">{i.size_human}</span>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkConfirm(null)}>Cancel</Button>
            <Button className="bg-rose-500 hover:bg-rose-600 text-white" onClick={performBulk}
              data-testid="bulk-confirm-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete {bulkConfirm?.items.length} Files · Free {humanBytes(bulkConfirm?.freeBytes || 0)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, val }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-200">{val}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <Badge className={`${STATUS_BADGE[status] || ""} border text-xs`}>
      {STATUS_LABEL[status] || status}
    </Badge>
  );
}

function humanBytes(n) {
  n = Number(n) || 0;
  for (const u of ["B", "KB", "MB", "GB", "TB"]) {
    if (n < 1024) return `${n.toFixed(1)} ${u}`;
    n /= 1024;
  }
  return `${n.toFixed(1)} PB`;
}

function FileTable({ rows, loading, kind, selection, toggleSelect, toggleAllOrphans, selectableRows, setPreview, setConfirm }) {
  return (
    <Card className="bg-zinc-950/60 border-zinc-800">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800">
              <TableHead className="w-8">
                <Checkbox
                  checked={selection.size > 0 && selection.size === selectableRows.length}
                  onCheckedChange={toggleAllOrphans}
                  data-testid="select-all-orphans"
                />
              </TableHead>
              {kind === "artwork" && <TableHead>Preview</TableHead>}
              <TableHead>File</TableHead>
              {kind === "artwork" && <TableHead>Dims</TableHead>}
              <TableHead>Size</TableHead>
              <TableHead>Song</TableHead>
              <TableHead>User</TableHead>
              <TableHead>ISRC / UPC</TableHead>
              <TableHead>Uploaded</TableHead>
              {kind === "audio" && <TableHead>Player</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={12} className="text-center text-zinc-500 py-6">Loading…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={12} className="text-center text-zinc-500 py-6">No files match your filter.</TableCell></TableRow>
            )}
            {rows.map((r) => {
              const primary = r.usage?.[0];
              const isOrphan = r.usage_status === "orphaned";
              const isMissing = r.usage_status === "missing_db";
              return (
                <TableRow key={`${r.kind}-${r.filename}`} className="border-zinc-800 hover:bg-zinc-900/40"
                  data-testid={`row-${r.filename}`}>
                  <TableCell>
                    <Checkbox
                      disabled={!isOrphan}
                      checked={selection.has(r.filename)}
                      onCheckedChange={() => isOrphan && toggleSelect(r.filename)}
                      data-testid={`select-${r.filename}`}
                    />
                  </TableCell>
                  {kind === "artwork" && (
                    <TableCell>
                      {isMissing ? (
                        <div className="w-12 h-12 rounded bg-zinc-800/50 flex items-center justify-center">
                          <FileWarning className="w-4 h-4 text-rose-400" />
                        </div>
                      ) : (
                        <button onClick={() => setPreview({ url: r.url, filename: r.filename })}
                          className="block w-12 h-12 rounded overflow-hidden border border-zinc-800 hover:ring-2 hover:ring-cyan-500/50 transition"
                          data-testid={`preview-${r.filename}`}>
                          <img src={fileUrl(r.url)} alt="" className="w-full h-full object-cover" />
                        </button>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="font-mono text-xs text-zinc-200 truncate max-w-[180px]" title={r.filename}>{r.filename}</div>
                    <div className="text-[10px] text-zinc-500">{r.format} · <span className="font-mono">{r.path}</span></div>
                  </TableCell>
                  {kind === "artwork" && <TableCell className="text-xs">{r.dimensions || "—"}</TableCell>}
                  <TableCell className="text-xs tabular-nums">{r.size_human}</TableCell>
                  <TableCell className="text-xs text-zinc-300 max-w-[160px] truncate">
                    {primary?.song_title || <span className="text-zinc-600">—</span>}
                    {primary?.submission_id && <div className="text-[10px] text-zinc-500 font-mono truncate">{primary.submission_id.slice(0, 8)}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-zinc-300 max-w-[140px] truncate">
                    {primary?.user_name || primary?.user_email || <span className="text-zinc-600">—</span>}
                  </TableCell>
                  <TableCell className="text-[11px] font-mono text-zinc-400">
                    <div>{primary?.isrc || "—"}</div>
                    <div>{primary?.upc || "—"}</div>
                  </TableCell>
                  <TableCell className="text-[11px] text-zinc-500">{fmtDate(r.modified)}</TableCell>
                  {kind === "audio" && (
                    <TableCell>
                      {isMissing ? <span className="text-xs text-rose-400">Missing</span> :
                        <InlineAudio url={r.url} testid={`audio-${r.filename}`} />
                      }
                    </TableCell>
                  )}
                  <TableCell><StatusBadge status={r.usage_status} /></TableCell>
                  <TableCell className="text-right">
                    {isOrphan && (
                      <Button size="sm" variant="outline"
                        className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                        onClick={() => setConfirm({ row: r, force: false })}
                        data-testid={`delete-${r.filename}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete File
                      </Button>
                    )}
                    {r.usage_status === "in_use" && (
                      <div className="flex items-center gap-1 justify-end">
                        <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 border text-[10px]">Protected</Badge>
                        <Button size="sm" variant="ghost"
                          className="text-rose-400 hover:bg-rose-500/10 text-xs"
                          onClick={() => setConfirm({ row: r, force: true })}
                          data-testid={`force-delete-${r.filename}`}>
                          Force
                        </Button>
                      </div>
                    )}
                    {isMissing && (
                      <Button size="sm" variant="outline"
                        className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                        onClick={() => setConfirm({ row: r, force: true })}
                        data-testid={`clear-ref-${r.filename}`}>
                        Clear DB Ref
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
